import {useCallback, useEffect, useState} from 'react';
import {Menu} from '@chakra-ui/react';
import {Archive, Copy, Download, FilePen, FolderInput, Image as ImageIcon, Star, Trash} from 'lucide-react';
import {DriveFile} from '../drive/types';
import {PlazmaStars} from '../hooks/usePlazmaStars';
import {DELETE_FORBIDDEN_MESSAGE} from '../domain/permissions';

interface MenuPosition {
    x: number;
    y: number;
    /** The element that was right-clicked, to tell whether the page under the menu has moved. */
    target: Element | null;
}

export interface ContextMenuControl {
    position: MenuPosition | null;
    isOpen: boolean;
    /** Use as an element's onContextMenu: replaces the browser's menu with ours, at the cursor. */
    open: (e: React.MouseEvent) => void;
    /** Opens it under an element instead, e.g. the viewer's "⋯" button on phones. */
    openBelow: (element: Element) => void;
    close: () => void;
}

export function useContextMenu(): ContextMenuControl {
    const [position, setPosition] = useState<MenuPosition | null>(null);
    const open = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setPosition({x: e.clientX, y: e.clientY, target: e.target instanceof Element ? e.target : null});
    }, []);
    const openBelow = useCallback((element: Element) => {
        const rect = element.getBoundingClientRect();
        setPosition({x: rect.left, y: rect.bottom, target: element});
    }, []);
    const close = useCallback(() => setPosition(null), []);
    return {position, isOpen: position !== null, open, openBelow, close};
}

export interface StarMenuAction {
    isStarred: boolean;
    isDisabled?: boolean;
    disabledReason?: string;
    onToggle: () => void;
}

/**
 * The star (copy to plazma) item for pictures in an album's web bin, like the cards' star buttons:
 * it un-stars them when every one is starred, and stars the rest otherwise.
 */
export function plazmaStarAction(plazma: PlazmaStars, files: DriveFile[]): StarMenuAction {
    const isStarred = files.every(plazma.isStarred);
    return {
        isStarred,
        isDisabled: !plazma.canToggleAll(files),
        // Un-starring deletes the plazma copies, which needs the right to delete them.
        disabledReason: isStarred && !plazma.canRemoveCopies(files) ? DELETE_FORBIDDEN_MESSAGE : undefined,
        onToggle: () => plazma.toggleAll(files),
    };
}

interface FileContextMenuProps {
    menu: ContextMenuControl;
    /** Omitted when there is nothing to download (e.g. only folders are selected). */
    onDownload?: () => void;
    /** Only for pictures in an album's web bin. */
    star?: StarMenuAction;
    /** Only for a single picture in an album's web bin (grid only). */
    setCover?: { disabledReason?: string; onSelect: () => void };
    onMoveToArchive?: () => void;
    onMove: () => void;
    /** Omitted when it doesn't apply (folders are selected, or in the viewer). */
    onCopy?: () => void;
    /** Omitted when it doesn't apply (several items are selected). */
    onRename?: () => void;
    onDelete: () => void;
    deleteDisabledReason?: string;
}

/**
 * The right-click menu of files and folders — the grid's selection or the viewer's picture —
 * offering the same actions as the grid's selection bar and the viewer's top bar.
 */
export function FileContextMenu(props: FileContextMenuProps) {
    const {position} = props.menu;
    if (!position) return null;
    // Keyed by position: a right-click elsewhere mounts a fresh menu there instead of moving this one.
    return <OpenFileContextMenu key={`${position.x}:${position.y}`} position={position} {...props}/>;
}

function OpenFileContextMenu({
    position,
    menu,
    onDownload,
    star,
    setCover,
    onMoveToArchive,
    onMove,
    onCopy,
    onRename,
    onDelete,
    deleteDisabledReason,
}: FileContextMenuProps & { position: MenuPosition }) {
    const {close} = menu;

    // Selecting by mouse or keyboard both end up here, by the item's value.
    const actions: Record<string, (() => void) | undefined> = {
        star: star?.onToggle,
        download: onDownload,
        cover: setCover?.onSelect,
        archive: onMoveToArchive,
        move: onMove,
        copy: onCopy,
        rename: onRename,
        delete: onDelete,
    };

    useEffect(() => {
        // A menu floating over content the user scrolled away (or a native menu elsewhere) is stale.
        // An applicable right-click opens a new menu, and has already prevented the default.
        const closeOnOtherContextMenu = (e: MouseEvent) => {
            if (!e.defaultPrevented) close();
        };
        // Not on every `scroll` event, though: opening the menu can scroll the page by itself. Right-
        // clicking with nothing selected shows the selection bar in the sticky header, and on a
        // scrolled page the browser's scroll anchoring then shifts scrollY by the bar's height — but
        // keeps the content in view still. So it closes only once the right-clicked element has moved.
        const {target} = position;
        const initialTop = target?.getBoundingClientRect().top;
        const closeIfContentMoved = () => {
            if (!target || Math.abs(target.getBoundingClientRect().top - (initialTop ?? 0)) > 1) close();
        };
        window.addEventListener('contextmenu', closeOnOtherContextMenu);
        window.addEventListener('resize', close);
        window.addEventListener('scroll', closeIfContentMoved, true);
        return () => {
            window.removeEventListener('contextmenu', closeOnOtherContextMenu);
            window.removeEventListener('resize', close);
            window.removeEventListener('scroll', closeIfContentMoved, true);
        };
    }, [close, position]);

    // Positioned at the cursor (anchorPoint). Not portalled, so that inside the viewer the menu stays
    // within the dialog's focus trap.
    return <Menu.Root
        open
        onOpenChange={(e) => {
            if (!e.open) close();
        }}
        onSelect={(e) => actions[e.value]?.()}
        anchorPoint={position}
        // The machine only repositions on anchorPoint changes; this menu mounts at its point.
        positioning={{
            placement: 'bottom-start',
            strategy: 'fixed',
            gutter: 2,
            // The (arrowless) arrow's padding would shift the menu this far left of a zero-width anchor.
            arrowPadding: 0,
            getAnchorRect: () => ({...position, width: 0, height: 0}),
        }}
    >
        <Menu.Positioner>
            <Menu.Content minW="220px" onContextMenu={(e) => e.preventDefault()}>
                {star &&
                    <Menu.Item value="star" disabled={star.isDisabled} title={star.disabledReason}>
                        <Star size={16} fill={star.isStarred ? 'currentColor' : 'none'}/>
                        {star.isStarred ? 'Eltávolítás a plazmából' : 'Plazmába másolás'}
                    </Menu.Item>
                }
                {setCover &&
                    <Menu.Item value="cover" disabled={!!setCover.disabledReason} title={setCover.disabledReason}>
                        <ImageIcon size={16}/>
                        Legyen borítókép
                    </Menu.Item>
                }
                {onMoveToArchive &&
                    <Menu.Item value="archive"><Archive size={16}/>Áthelyezés az archívumba</Menu.Item>
                }
                {star || setCover || onMoveToArchive
                    ? <Menu.Separator/>
                    : null
                }
                {onDownload &&
                    <Menu.Item value="download"><Download size={16}/>Letöltés</Menu.Item>
                }
                <Menu.Item value="move"><FolderInput size={16}/>Áthelyezés…</Menu.Item>
                {onCopy &&
                    <Menu.Item value="copy"><Copy size={16}/>Másolás…</Menu.Item>
                }
                {onRename &&
                    <Menu.Item value="rename"><FilePen size={16}/>Átnevezés</Menu.Item>
                }
                <Menu.Separator/>
                <Menu.Item
                    value="delete"
                    color="red.500"
                    disabled={!!deleteDisabledReason}
                    title={deleteDisabledReason}
                >
                    <Trash size={16}/>
                    Törlés
                </Menu.Item>
            </Menu.Content>
        </Menu.Positioner>
    </Menu.Root>;
}
